import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material';
import { ToastrService } from 'ngx-toastr';
import { of, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import {
  AdminBlogCategory,
  AdminBlogTag,
  BlogPostCreateRequest
} from '@app/model/blog-management.model';
import {
  BLOG_POST_FORM_LANGUAGES,
  BlogPostFormLanguage,
  BlogPostFormText
} from '../blog-post-form-language.config';
import { BlogManagementDataService } from '@app/services/blog-management-data.service';
import { take } from 'rxjs/operators';

interface BlogLanguage {
  code: string;
  label: string;
  placeholder: string;
}

@Component({
  selector: 'meu-blog-post-create',
  templateUrl: './blog-post-create.component.html',
  styleUrls: ['./blog-post-create.component.scss']
})
export class BlogPostCreateComponent implements OnInit {
  isSaving = false;
  languages: BlogPostFormLanguage[] = BLOG_POST_FORM_LANGUAGES;

  selectedLanguageIndex = 0;
  languageForms: { [languageCode: string]: FormGroup } = {
    vi: this.createLanguageForm(),
    en: this.createLanguageForm(),
    zh: this.createLanguageForm()
  };

  settingsForm = new FormGroup({
    categoryId: new FormControl('', [Validators.required]),
    tagIds: new FormControl([]),
    author: new FormControl(''),
    isPublished: new FormControl(false),
    isHot: new FormControl(false),
    isFeatured: new FormControl(false)
  });

  categories: AdminBlogCategory[] = [];
  categoryLoadError = false;

  tags: AdminBlogTag[] = [];
  isLoadingTags = false;
  tagLoadError = false;

  isLoadingOptions = false;

  selectedImageFile: File | null = null;
  imagePreviewUrl = '';

  editorConfig = {
    placeholder: 'Nhập nội dung bài viết',
    tabsize: 2,
    height: 'auto',
    minHeight: '45vh',
    dialogsInBody: true,
    uploadImagePath: '/api/upload',
    focus: false,
    tooltip: false,
    fontNamesIgnoreCheck: [
      'Helvetica',
      'Arial',
      'Arial Black',
      'Comic Sans MS',
      'Courier New',
      'Roboto',
      'Times'
    ],
    toolbar: [
      ['misc', ['codeview', 'undo', 'redo']],
      [
        'font',
        ['bold', 'italic', 'underline', 'strikethrough', 'superscript', 'subscript', 'clear']
      ],
      ['fontsize', ['fontname', 'fontsize', 'color']],
      ['para', ['style', 'ul', 'ol', 'paragraph', 'height']],
      ['insert', ['table', 'picture', 'link', 'video', 'hr']],
      ['view', ['fullscreen', 'codeview', 'help']]
    ]
  };

  constructor(
    private dialogRef: MatDialogRef<BlogPostCreateComponent>,
    private blogManagementDataService: BlogManagementDataService,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.loadCategories();
    this.loadTags();
  }

  closeModal() {
    this.dialogRef.close();
  }
  savePost() {
    const vietnameseForm = this.languageForms.vi;
    const englishForm = this.languageForms.en;
    const chineseForm = this.languageForms.zh;

    const languageFormEntries = [
      {
        code: 'vi',
        index: 0,
        label: 'Tiếng Việt',
        form: vietnameseForm
      },
      {
        code: 'en',
        index: 1,
        label: 'English',
        form: englishForm
      },
      {
        code: 'zh',
        index: 2,
        label: '中文',
        form: chineseForm
      }
    ];

    // const invalidLanguage = languageFormEntries.find((language) => language.form.invalid);

    // if (invalidLanguage || this.settingsForm.invalid) {
    //   languageFormEntries.forEach((language) => {
    //     language.form.markAllAsTouched();
    //   });

    //   this.settingsForm.markAllAsTouched();

    //   if (invalidLanguage) {
    //     this.selectedLanguageIndex = invalidLanguage.index;

    //     this.toastr.error('Vui lòng nhập đầy đủ nội dung tab ' + invalidLanguage.label);

    //     return;
    //   }

    //   this.toastr.error('Vui lòng chọn danh mục');

    //   return;
    // }

    // const vietnameseContent = vietnameseForm.value;
    // const englishContent = englishForm.value;
    // const chineseContent = chineseForm.value;
    // const settings = this.settingsForm.value;

    // const payload: BlogPostCreateRequest = {
    //   post: {
    //     category_id: settings.categoryId,
    //     title: englishContent.title,
    //     slug: englishContent.slug,
    //     excerpt: englishContent.excerpt,
    //     content: englishContent.content,
    //     featured_image: '',
    //     author: settings.author.trim(),
    //     is_published: settings.isPublished,
    //     is_hot: settings.isHot,
    //     is_featured: settings.isFeatured,
    //     published_at: settings.isPublished ? new Date().toISOString() : null
    //   },
    //   translations: [
    //     {
    //       language_code: 'vi',
    //       title: vietnameseContent.title,
    //       slug: vietnameseContent.slug,
    //       excerpt: vietnameseContent.excerpt,
    //       content: vietnameseContent.content
    //     },
    //     {
    //       language_code: 'en',
    //       title: englishContent.title,
    //       slug: englishContent.slug,
    //       excerpt: englishContent.excerpt,
    //       content: englishContent.content
    //     },
    //     {
    //       language_code: 'zh',
    //       title: chineseContent.title,
    //       slug: chineseContent.slug,
    //       excerpt: chineseContent.excerpt,
    //       content: chineseContent.content
    //     }
    //   ],
    //   tags: settings.tagIds || []
    // };

    const filledLanguageEntries = languageFormEntries.filter((language) =>
      this.hasLanguageContent(language.form)
    );

    if (filledLanguageEntries.length === 0) {
      this.toastr.error('Vui lòng nhập nội dung cho ít nhất một ngôn ngữ');

      return;
    }

    const invalidLanguage = filledLanguageEntries.find((language) => language.form.invalid);

    if (invalidLanguage) {
      this.markFormTouched(invalidLanguage.form);
      this.selectedLanguageIndex = invalidLanguage.index;

      this.toastr.error('Vui lòng nhập đầy đủ nội dung tab ' + invalidLanguage.label);

      return;
    }

    if (this.settingsForm.invalid) {
      this.settingsForm.markAllAsTouched();
      this.toastr.error('Vui lòng chọn danh mục');

      return;
    }

    const primaryLanguage =
      filledLanguageEntries.find((language) => language.code === 'en') || filledLanguageEntries[0];

    const primaryContent = primaryLanguage.form.value;
    const settings = this.settingsForm.value;

    const payload: BlogPostCreateRequest = {
      post: {
        category_id: settings.categoryId,
        title: primaryContent.title,
        slug: primaryContent.slug,
        excerpt: primaryContent.excerpt,
        content: primaryContent.content,
        featured_image: '',
        author: (settings.author || '').trim(),
        is_published: settings.isPublished,
        is_hot: settings.isHot,
        is_featured: settings.isFeatured,
        published_at: settings.isPublished ? new Date().toISOString() : null
      },

      translations: filledLanguageEntries.map((language) => {
        const content = language.form.value;

        return {
          language_code: language.code,
          title: content.title,
          slug: content.slug,
          excerpt: content.excerpt,
          content: content.content
        };
      }),

      tags: settings.tagIds || []
    };

    this.isSaving = true;

    this.blogManagementDataService
      .createPost(payload)
      .pipe(
        switchMap((response) => {
          const createdPostId = response && response.data ? response.data.id : null;

          if (!response || !response.success || !createdPostId || !this.selectedImageFile) {
            return of(response);
          }

          return this.blogManagementDataService
            .uploadPostImage(createdPostId, this.selectedImageFile)
            .pipe(
              map((uploadResponse) => {
                if (!uploadResponse || !uploadResponse.success) {
                  this.toastr.warning('Bài viết đã tạo nhưng không thể tải ảnh đại diện');
                }

                return response;
              }),
              catchError(() => {
                this.toastr.warning('Bài viết đã tạo nhưng không thể tải ảnh đại diện');

                return of(response);
              })
            );
        }),
        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe(
        (response) => {
          const createdPostId = response && response.data ? response.data.id : null;

          if (!response || !response.success) {
            this.toastr.error(
              response && response.message ? response.message : 'Không thể tạo bài viết'
            );

            return;
          }

          if (!createdPostId) {
            this.toastr.warning('BE báo thành công nhưng không trả về mã bài viết');

            return;
          }

          this.toastr.success('Tạo bài viết thành công');

          this.dialogRef.close({
            created: true,
            postId: createdPostId
          });
        },
        (error) => {
          console.error('Create blog post failed:', error);

          const errorResponse = error && error.error ? error.error : null;

          let message = 'Không thể tạo bài viết';

          if (errorResponse && errorResponse.message) {
            message = errorResponse.message;
          } else if (errorResponse && errorResponse.title) {
            message = errorResponse.title;
          }

          this.toastr.error(message);
        }
      );
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || !input.files.length) {
      return;
    }

    const imageFile = input.files[0];

    if (imageFile.type.indexOf('image/') !== 0) {
      this.toastr.error('Vui lòng chọn đúng định dạng hình ảnh');
      input.value = '';

      return;
    }

    this.selectedImageFile = imageFile;

    const reader = new FileReader();

    reader.onload = () => {
      this.imagePreviewUrl = typeof reader.result === 'string' ? reader.result : '';
    };

    reader.readAsDataURL(imageFile);
  }

  onRemoveImage(): void {
    this.selectedImageFile = null;
    this.imagePreviewUrl = '';
  }

  private createLanguageForm(): FormGroup {
    return new FormGroup({
      title: new FormControl('', [Validators.required]),
      slug: new FormControl('', [Validators.required]),
      excerpt: new FormControl('', [Validators.required]),
      content: new FormControl('', [Validators.required])
    });
  }

  private loadCategories() {
    this.isLoadingOptions = true;
    this.categoryLoadError = false;

    this.blogManagementDataService
      .getCategories()
      .pipe(take(1))
      .subscribe(
        (categories) => {
          this.categories = categories;
          this.isLoadingOptions = false;
        },
        () => {
          this.categories = [];
          this.isLoadingOptions = false;
          this.categoryLoadError = true;
        }
      );
  }

  loadTags() {
    this.isLoadingTags = true;
    this.tagLoadError = false;

    this.blogManagementDataService
      .getTags()
      .pipe(
        take(1),
        finalize(() => {
          this.isLoadingTags = false;
        })
      )
      .subscribe(
        (tags) => {
          this.tags = tags;
        },
        (error) => {
          console.error('Load blog tags failed:', error);

          this.tags = [];
          this.tagLoadError = true;
        }
      );
  }

  private hasLanguageContent(form: FormGroup): boolean {
    const value = form.value;

    return [value.title, value.slug, value.excerpt, value.content].some(
      (item) => typeof item === 'string' && item.trim().length > 0
    );
  }

  private markFormTouched(form: FormGroup): void {
    Object.keys(form.controls).forEach((controlName) => {
      form.controls[controlName].markAsTouched();
    });
  }

  get activeLanguage(): BlogPostFormLanguage {
    return this.languages[this.selectedLanguageIndex] || this.languages[0];
  }

  get activeText(): BlogPostFormText {
    return this.activeLanguage.text;
  }
}
